import URL from 'url-parse';
import getConfig from 'next/config';

import rollbar from '~/src/utilities/libs/rollbar';
import {loadExternalDependency, waitForCondition} from '~/src/utilities/helpers/dependency';

const {publicRuntimeConfig: config} = getConfig();

const facebookSDKDependencyLoaded = () => window.FB && window.FB.init;
const twitterWidgetsDependencyLoaded = () => window.twttr && window.twttr.widgets;
const instagramEmbedsDependencyLoaded = () => window.instgrm && window.instgrm.Embeds;
const tiktokEmbedsDependencyLoaded = () => window.tiktokEmbed && window.tiktokEmbed.lib && window.tiktokEmbed.lib.render;

const loadFacebookSDKDependency = async () => {
    if (facebookSDKDependencyLoaded()) {
        return;
    }

    await loadExternalDependency({src: config.scripts.facebook});
    await waitForCondition(facebookSDKDependencyLoaded, 5000);

    window.FB.init({
        appId: config.socialMedia.facebook.appId,
        status: true,
        xfbml: true,
        version: config.socialMedia.facebook.version
    });
};

const loadTwitterWidgetsDependency = async () => {
    if (twitterWidgetsDependencyLoaded()) {
        return;
    }

    await loadExternalDependency({src: config.scripts.twitter});
    await waitForCondition(twitterWidgetsDependencyLoaded, 5000);
};

const loadInstagramEmbedsDependency = async () => {
    if (instagramEmbedsDependencyLoaded()) {
        return;
    }

    await loadExternalDependency({src: config.scripts.instagram});
    await waitForCondition(instagramEmbedsDependencyLoaded, 5000);
};

const loadTiktokDependency = async () => {
    if (tiktokEmbedsDependencyLoaded()) {
        return;
    }

    await loadExternalDependency({src: config.scripts.tiktok});
    await waitForCondition(tiktokEmbedsDependencyLoaded, 5000);
};

const loadTwitterEmbed = async (url, container) => {
    try {
        await loadTwitterWidgetsDependency();
        window.twttr.widgets.createTweet && window.twttr.widgets.createTweet(new URL(url).pathname.split('/').pop(), container);
    } catch (err) {
        rollbar.error('Error while loading twitter embed', err);
    }
};

const loadFacebookEmbed = async () => {
    try {
        await loadFacebookSDKDependency();
        window.FB.XFBML && window.FB.XFBML.parse();
    } catch (err) {
        rollbar.error('Error while loading facebook embed', err);
    }
};

const loadInstagramEmbed = async () => {
    try {
        await loadInstagramEmbedsDependency();
        window.instgrm.Embeds.process && window.instgrm.Embeds.process();
    } catch (err) {
        rollbar.error('Error while loading instagram embed', err);
    }
};

const loadTiktokEmbeds = async (nodeList) => {
    try {
        await loadTiktokDependency();
        window.tiktokEmbed.lib.render([...nodeList]);
    } catch (err) {
        rollbar.error('Error while loading tiktok embed', err);
    }
};

const openFacebookShare = async (urlString) => {
    try {
        await loadFacebookSDKDependency();
        const url = new URL(urlString);
        window.FB.ui &&
            window.FB.ui({
                method: 'share',
                app_id: config.socialMedia.facebook.appId,
                display: 'popup',
                href: url.href
            });
    } catch (err) {
        rollbar.error('Error while opening facebook share', err);
    }
};

export {loadTwitterEmbed, loadFacebookEmbed, loadInstagramEmbed, loadTiktokEmbeds, openFacebookShare};
